import { useEffect, useState, useCallback } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Bse2Badge } from "@/components/Bse2Badge";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";

export default function OrdersMonitor() {
  const [orderType, setOrderType] = useState("");
  const [status, setStatus] = useState("");
  const [clientCode, setClientCode] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 50 };
      if (orderType && orderType !== "SWITCH") params.order_type = orderType;
      if (status) params.status = status;
      if (clientCode.trim()) params.client_code = clientCode.trim();
      if (orderType === "SWITCH") {
        const { data: switches } = await adminApi.get("/admin/switches");
        const items = switches.filter((item) => !clientCode.trim() || item.client_code === clientCode.trim());
        setData({ items, total: items.length });
      } else {
        const { data } = await adminApi.get("/admin/orders", { params });
        setData(data);
      }
    } finally { setLoading(false); }
  }, [orderType, status, clientCode]);

  useEffect(() => { load(); }, [load]);


  const exportCsv = async () => {
    try {
      const response = await adminApi.get("/admin/export/orders.csv", { responseType: "blob" });
      const blob = response.data;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "orders_export.csv"; a.click();
        URL.revokeObjectURL(url);
        toast.success("Order CSV downloaded");
    } catch (error) { toast.error(getApiErrorMessage(error)); }
  };

  const refreshBse = async (orderNumber) => {
    try {
      const { data: result } = await adminApi.post(`/admin/orders/${orderNumber}/bse-refresh`);
      toast.success(result.message || `BSE status: ${result.live_status || "refreshed"}`);
      load();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-3xl text-[var(--brand)]">Orders</h1>
        <Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="admin-orders-export-button"><Download className="mr-2 h-4 w-4" />Export CSV</Button>
      </div>

      <Card className="mb-4 border-[var(--glass-border)]">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row">
          <Select value={orderType || "__all"} onValueChange={(value) => setOrderType(value === "__all" ? "" : value)}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="admin-orders-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All types</SelectItem><SelectItem value="PURCHASE">Purchase</SelectItem><SelectItem value="REDEMPTION">Redemption</SelectItem><SelectItem value="SWITCH">Switch</SelectItem></SelectContent>
          </Select>
          <Select value={status || "__all"} onValueChange={(value) => setStatus(value === "__all" ? "" : value)}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="admin-orders-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All statuses</SelectItem>
              {["RECEIVED", "PAYMENT_PENDING", "PAYMENT_CONFIRMED", "SENT_TO_RTA", "ALLOTTED", "REJECTED"].map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="w-full sm:w-[220px]" value={clientCode} onChange={(e) => setClientCode(e.target.value.toUpperCase())} placeholder="Filter by client code" />
        </CardContent>
      </Card>

      <Card className="border-[var(--glass-border)]">
        <CardContent className="p-0" data-testid="admin-orders-table">
          {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading...</div> :
            data.items.length === 0 ? <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid="admin-orders-empty">No orders found.</div> :
            <div className="divide-y divide-[var(--glass-border)]">
              {data.items.map((o) => (
                <div key={o.order_number || o.order_id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-order-row-${o.order_number || o.order_id}`}>
                  <div>
                    <div className="font-medium">{o.scheme_name || `${o.from_scheme_name || o.from_scheme_cd} → ${o.to_scheme_name || o.to_scheme_cd}`}</div>
                    <div className="text-xs text-[var(--ink-muted)]">Client {o.client_code} &middot; Order #{o.order_number || o.order_id} &middot; {formatDateTime(o.created_at)}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:gap-3">
                    <div className="font-medium">{o.amount || o.switch_amount ? formatCurrency(o.amount || o.switch_amount) : `${o.qty || o.switch_units || "—"} units`}</div>
                    <StatusBadge status={o.status} />
                    <Bse2Badge bse2={o.bse2} />
                    {o.order_number && o.bse2?.bse_order_id && <Button size="sm" variant="outline" className="rounded-full" onClick={() => refreshBse(o.order_number)}><RefreshCw className="mr-1 h-3.5 w-3.5" /> BSE status</Button>}
                  </div>
                </div>
              ))}
            </div>}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
